import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type {
  BatchOperationItemResult,
  BatchOperationPhase,
  BatchOperationSummary,
} from '../../lib/batchOperations';
import { OperationProgressPanel, type OperationProgressModel } from '../progress';
import { previewDockPaperSx, previewDockSx } from '../dialogs/previewDockSx';

export interface BatchOperationDialogProps {
  open: boolean;
  title: string;
  phase?: BatchOperationPhase;
  contentRef?: React.Ref<HTMLDivElement>;
  contentTestId?: string;
  previewOnly?: boolean;
  warning?: React.ReactNode;
  description?: React.ReactNode;
  progressModel?: OperationProgressModel | null;
  summary: BatchOperationSummary | null;
  successItems?: BatchOperationItemResult[];
  failedItems?: BatchOperationItemResult[];
  onClose: () => void;
  onContinue?: () => void;
  onConfirm?: () => void;
  closeLabel?: string;
  continueLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  showItemDetails?: boolean;
}

const isProgressPhase = (phase?: BatchOperationPhase): boolean =>
  phase === 'queued' || phase === 'running' || phase === 'polling' || phase === 'refreshing';

const progressMessageForPhase = (phase: BatchOperationPhase): string => {
  switch (phase) {
    case 'queued':
      return 'Queued for Revit';
    case 'running':
      return 'Batch operation is running';
    case 'polling':
      return 'Waiting for host completion';
    case 'refreshing':
      return 'Refreshing affected data';
    default:
      return 'Batch operation is running';
  }
};

const buildProgressModel = (
  title: string,
  phase: BatchOperationPhase,
  description: React.ReactNode,
  summary: BatchOperationSummary | null
): OperationProgressModel => {
  const processed = summary
    ? summary.succeeded + summary.failed + (summary.warnings ?? 0) + (summary.skipped ?? 0)
    : undefined;
  const total = summary?.total;

  return {
    title,
    message: typeof description === 'string' ? description : progressMessageForPhase(phase),
    detail: progressMessageForPhase(phase),
    status: 'running',
    processed,
    total,
    percent: null,
    locked: true,
  };
};

const itemText = (value: React.ReactNode): React.ReactNode => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  return value;
};

export const BatchOperationDialog: React.FC<BatchOperationDialogProps> = ({
  open,
  title,
  phase = 'idle',
  contentRef,
  contentTestId,
  previewOnly = false,
  warning,
  description,
  progressModel: backendProgressModel,
  summary,
  successItems,
  failedItems,
  onClose,
  onContinue,
  onConfirm,
  closeLabel = 'Close',
  continueLabel = 'Continue',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  showItemDetails = true,
}) => {
  const progress = isProgressPhase(phase);
  const progressModel = progress
    ? backendProgressModel ?? buildProgressModel(title, phase, description, summary)
    : null;
  const allItems = summary?.items ?? [];
  const resolvedSuccessItems = successItems ?? allItems.filter((item) => item.status === 'success');
  const resolvedFailedItems =
    failedItems ?? allItems.filter((item) => item.status === 'failed' || item.status === 'warning');
  const previewAccordionSx: SxProps<Theme> = {
    '&.Mui-expanded': { m: { xs: 0, sm: 2 } },
  };

  const handleClose = () => {
    if (previewOnly || progress) {
      return;
    }

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={previewOnly ? 'sm' : 'md'}
      fullWidth
      aria-labelledby="batch-operation-dialog-title"
      slotProps={{
        container: { sx: previewDockSx(previewOnly) },
        paper: {
          sx: {
            m: previewOnly ? { xs: 1, sm: 4 } : undefined,
            ...previewDockPaperSx(previewOnly),
          },
        },
      }}
    >
      <DialogTitle
        id="batch-operation-dialog-title"
        sx={{ py: previewOnly ? { xs: 1, sm: 2 } : undefined }}
      >
        {title}
      </DialogTitle>
      <DialogContent
        dividers
        ref={contentRef}
        data-testid={contentTestId}
        sx={{ py: previewOnly ? { xs: 1, sm: 2 } : undefined }}
      >
        {progressModel ? (
          <Box sx={{ mb: 2 }}>
            <OperationProgressPanel model={progressModel} compact />
            {description && typeof description !== 'string' ? (
              <Box sx={{ mt: 1, color: 'text.secondary' }}>{description}</Box>
            ) : null}
          </Box>
        ) : null}
        {!progress && description ? (
          <Box sx={{ mb: 2, color: 'text.secondary' }}>
            {description}
          </Box>
        ) : null}
        {warning ? (
          <Alert severity="warning" sx={{ mb: previewOnly ? { xs: 1, sm: 2 } : 2 }}>
            {warning}
          </Alert>
        ) : null}
        {summary ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: previewOnly ? { xs: 1, sm: 2 } : 2,
              flexWrap: 'wrap',
            }}
          >
            <Chip label={`Total: ${summary.total}`} />
            <Chip color="success" label={`Success: ${summary.succeeded}`} />
            {(summary.warnings ?? 0) > 0 ? (
              <Chip color="warning" label={`Warning: ${summary.warnings}`} />
            ) : null}
            {(summary.skipped ?? 0) > 0 ? (
              <Chip label={`Skipped: ${summary.skipped}`} />
            ) : null}
            <Chip color="error" label={`Failed: ${summary.failed}`} />
          </Box>
        ) : null}
        {summary?.message ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {summary.message}
          </Typography>
        ) : null}
        {showItemDetails ? (
          <>
            <Accordion
              defaultExpanded={resolvedFailedItems.length > 0}
              sx={previewOnly ? previewAccordionSx : undefined}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Failed Items ({resolvedFailedItems.length})</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ maxHeight: 260, overflow: 'auto', p: 0 }}>
                <List dense>
                  {resolvedFailedItems.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No failed items" />
                    </ListItem>
                  ) : null}
                  {resolvedFailedItems.map((item, idx) => (
                    <ListItem key={item.id || `f-${idx}`} divider>
                      <ListItemText
                        primary={itemText(item.label)}
                        secondary={itemText(item.error || item.detail)}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
            <Accordion sx={previewOnly ? previewAccordionSx : undefined}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Successful Items ({resolvedSuccessItems.length})</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ maxHeight: 260, overflow: 'auto', p: 0 }}>
                <List dense>
                  {resolvedSuccessItems.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No successful items" />
                    </ListItem>
                  ) : null}
                  {resolvedSuccessItems.map((item, idx) => (
                    <ListItem key={item.id || `s-${idx}`} divider>
                      <ListItemText primary={itemText(item.label)} secondary={itemText(item.detail)} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        {previewOnly ? (
          <>
            <Button disabled>{closeLabel}</Button>
            {(onContinue || onConfirm) ? (
              <Button variant="contained" disabled>
                {onConfirm ? confirmLabel : continueLabel}
              </Button>
            ) : null}
          </>
        ) : onConfirm ? (
          <>
            <Button onClick={onClose} disabled={progress}>
              {cancelLabel}
            </Button>
            <Button variant="contained" onClick={onConfirm} disabled={progress}>
              {confirmLabel}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={progress}>
              {closeLabel}
            </Button>
            {onContinue ? (
              <Button variant="contained" onClick={onContinue} disabled={progress}>
                {continueLabel}
              </Button>
            ) : null}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
